import { computed, ref, watch, type Ref, type ComputedRef } from 'vue'
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useCore } from '@/composables/core'
import { useEppo, whenEppoIsReady } from '@/composables/eppo'
import { FEATURE_FLAGS } from '@/lib/consts.ts'
import { shouldDisableCustomInvoices } from '@/views/business/settings/payments/verifactu/custom-invoice-gate.ts'

function isDueInvoicingFlagEnabled (value?: boolean | null): boolean {
  if (value != null) {
    return value === true
  }

  return useEppo().ff(FEATURE_FLAGS.Verifactu_due_invoicing_changes) === true
}

export async function resolveShouldDisableCustomInvoices (options?: {
  businessId?: number | string | null
  country?: string | null
  verifactuEnabled?: boolean | null
  dueInvoicingChangesEnabled?: boolean | null
  force?: boolean
}): Promise<boolean> {
  await whenEppoIsReady

  const { store } = useCore()
  const business = store.getters['business/business'] as {
    id?: number | string
    verifactu_enabled?: boolean
  } | null

  const businessId = options?.businessId ?? business?.id
  const country = options?.country ?? (store.getters['app/country'] as string | undefined)
  const verifactuEnabled = options?.verifactuEnabled ?? business?.verifactu_enabled
  const dueInvoicingChangesEnabled = isDueInvoicingFlagEnabled(options?.dueInvoicingChangesEnabled)

  if (
    !businessId
    || country !== 'es'
    || verifactuEnabled !== true
    || dueInvoicingChangesEnabled !== true
  ) {
    return false
  }

  if (options?.force || !store.getters['business/verifactuRegistrationLoaded']) {
    await store.dispatch('business/loadVerifactuRegistration', { force: options?.force === true })
  }

  const adapterStatus = store.getters['business/verifactuRegistrationStatus'] as string | null

  return shouldDisableCustomInvoices({
    country,
    verifactuEnabled,
    adapterStatus,
    dueInvoicingChangesEnabled: true,
  })
}

/** @deprecated Prefer store dispatch `business/loadVerifactuRegistration({ force: true })`. */
export function clearCustomInvoiceGateCache (businessId?: number | string): void {
  const { store } = useCore()
  if (businessId == null || String(store.getters['business/businessId']) === String(businessId)) {
    store.commit('business/resetVerifactuRegistration')
  }
}

export type UseVerifactuCustomInvoiceGateReturn = {
  customInvoicesDisabled: Ref<boolean>
  customInvoicesGateReady: Ref<boolean>
  isEligibleForGate: ComputedRef<boolean>
  refresh: () => Promise<boolean>
}

export function useVerifactuCustomInvoiceGate (): UseVerifactuCustomInvoiceGateReturn {
  const { store } = useCore()

  const business = computed(() => store.getters['business/business'] as {
    id?: number | string
    verifactu_enabled?: boolean
  } | null)
  const country = computed(() => store.getters['app/country'] as string | undefined)
  const registrationLoaded = computed(() => store.getters['business/verifactuRegistrationLoaded'] as boolean)
  const registrationStatus = computed(() => store.getters['business/verifactuRegistrationStatus'] as string | null)

  const dueInvoicingChangesEnabled = ref(false)

  const customInvoicesDisabled = ref(false)
  const customInvoicesGateReady = ref(false)

  const isEligibleForGate = computed(() => (
    country.value === 'es'
    && business.value?.verifactu_enabled === true
    && dueInvoicingChangesEnabled.value
  ))

  const refresh = async (): Promise<boolean> => {
    await whenEppoIsReady
    dueInvoicingChangesEnabled.value = isDueInvoicingFlagEnabled()

    if (!isEligibleForGate.value || !business.value?.id) {
      customInvoicesDisabled.value = false
      customInvoicesGateReady.value = true
      return false
    }

    const disabled = await resolveShouldDisableCustomInvoices({
      businessId: business.value.id,
      country: country.value,
      verifactuEnabled: business.value.verifactu_enabled,
      dueInvoicingChangesEnabled: true,
    })

    customInvoicesDisabled.value = disabled
    customInvoicesGateReady.value = true
    return disabled
  }

  watch(
    () => [
      business.value?.id,
      business.value?.verifactu_enabled,
      country.value,
      registrationLoaded.value,
      registrationStatus.value,
    ] as const,
    () => {
      void refresh()
    },
    { immediate: true }
  )

  return {
    customInvoicesDisabled,
    customInvoicesGateReady,
    isEligibleForGate,
    refresh,
  }
}

export async function verifactuCustomInvoiceIssuanceBeforeEnter (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const disabled = await resolveShouldDisableCustomInvoices()

  if (disabled) {
    next({
      name: 'sales.invoices.list',
      params: {
        businessId: to.params.businessId,
      },
    })
    return
  }

  next()
}
